import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { SentinelTicker } from '../components/SentinelTicker';
import { api } from '../services/api';
import { 
  BookOpen, TrendingUp, TrendingDown, ExternalLink, Shield, 
  Layers, Globe, ArrowRight, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const marketStats = [
  { label: 'Global RWA Market Cap', value: '$29.3B', delta: '+266% Year-over-Year' },
  { label: 'BlackRock BUIDL Fund AUM', value: '$1.7B', delta: 'Largest tokenized fund' },
  { label: 'Luxury Products On-Chain', value: '40M', delta: 'Aura Blockchain Consortium' },
  { label: 'NYSE Tokenized Trading', value: '24/7', delta: 'Always-on capital markets' },
];

const ChartTooltip = ({ active, payload, prefix, suffix }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-[#333] p-2 rounded text-xs">
      <p className="text-white">{prefix || ''}{payload[0].value?.toLocaleString()}{suffix || ''}</p>
    </div>
  );
};

const CompositeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-[#333] px-3 py-2 rounded shadow-lg">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs flex items-center gap-2" style={{ color: p.color }}>
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-medium">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const Sparkline = ({ data, color }) => {
  if (!data?.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / range) * 100}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-10">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export default function Intelligence() {
  const [newsArticles, setNewsArticles] = useState([]);
  const [charts, setCharts] = useState({});
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    fetchNews();
    fetchCharts();
  }, []);

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/market/intelligence-news`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) setNewsArticles(data);
      }
    } catch {}
    setNewsLoading(false);
  };

  const fetchCharts = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      const [composite, grid] = await Promise.all([
        fetch(`${API}/api/market/chart-data/luxury-composite`).then(r => r.json()),
        fetch(`${API}/api/market/chart-data/category-grid`).then(r => r.json()),
      ]);
      setCharts({ composite, grid });
    } catch {}
  };

  return (
    <DashboardLayout>
      <div data-testid="intelligence-page">
        {/* Sentinel Ticker */}
        <SentinelTicker />

        <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-12">
          {/* Header */}
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Market Intelligence</p>
            <h1 className="font-serif text-3xl text-white">Global Intelligence Portal</h1>
            <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
              Stay informed with curated market intelligence, tokenization updates, and the latest developments in luxury asset management.
            </p>
          </div>

          {/* Executive Briefing */}
          <section className="luxury-card p-6 lg:p-8" data-testid="executive-briefing">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={16} className="text-gold" />
              <span className="text-xs text-gold uppercase tracking-[0.2em]">Executive Briefing, Q2 2026</span>
            </div>
            <h2 className="font-serif text-2xl text-white mb-4">
              Luxury Assets Are Becoming the Hard Capital of the Digital Age
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              The convergence of distributed ledger technology and institutional-grade asset management has created an inflection point. Real-world asset tokenization has transcended proof-of-concept and entered the domain of sovereign wealth funds, family offices, and blue-chip corporates. What was once a speculative thesis is now an operating reality. Luxury assets, from ultra-prime collectibles to provenance-verified timepieces, are being digitally anchored into programmable, composable capital instruments.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {marketStats.map((stat, i) => (
                <div key={i} className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded">
                  <p className="text-gold font-serif text-2xl">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                  <p className="text-[10px] text-emerald-400 mt-1">{stat.delta}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Live Charts */}
          <section data-testid="live-charts">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} className="text-gold" />
              <h2 className="font-serif text-xl text-white">Live Market Data</h2>
            </div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-6">
              Benchmarks curated for the wealth-management lens
            </p>

            {/* Hero Chart - Luxury Composite vs S&P 500 vs Gold */}
            <div className="luxury-card p-5 lg:p-6 mb-6" data-testid="chart-luxury-composite">
              <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
                <div>
                  <p className="text-white font-serif text-base lg:text-lg">
                    {charts.composite?.title || 'Luxury Composite Index vs S&P 500 vs Gold'}
                  </p>
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wider mt-1">
                    {charts.composite?.subtitle || 'Indexed to 100, 24-month performance'}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" /> Luxury Composite
                  </span>
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-[#888]" /> S&amp;P 500
                  </span>
                  <span className="flex items-center gap-1.5 text-amber-300">
                    <span className="w-2 h-2 rounded-full bg-amber-300" /> Gold
                  </span>
                </div>
              </div>
              <div className="h-[280px] mt-4">
                {charts.composite?.data ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts.composite.data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#1A1A1A" vertical={false} />
                      <XAxis dataKey="label" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} axisLine={{ stroke: '#333' }} interval={3} />
                      <YAxis stroke="#666" tick={{ fill: '#999', fontSize: 10 }} axisLine={{ stroke: '#333' }} domain={['dataMin - 2', 'dataMax + 2']} />
                      <Tooltip content={<CompositeTooltip />} />
                      <Line type="monotone" dataKey="luxury" name="Luxury Composite" stroke="#34D399" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                      <Line type="monotone" dataKey="sp500" name="S&P 500" stroke="#888888" strokeWidth={1.75} dot={false} strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="gold" name="Gold (LBMA)" stroke="#FCD34D" strokeWidth={1.75} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Skeleton className="h-full w-full bg-white/5" />
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-3 italic">
                Sources: Knight Frank Luxury Investment Index, S&amp;P Dow Jones, LBMA. Indexed for relative comparison.
              </p>
            </div>

            {/* Category Performance Grid */}
            <div className="mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                {charts.grid?.title || 'Category Performance YTD'}
              </p>
              <p className="text-[11px] text-muted-foreground/70 mt-1">
                {charts.grid?.subtitle || 'Year-to-date performance across luxury asset categories'}
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="category-performance-grid">
              {(charts.grid?.data || []).map((cat) => {
                const positive = cat.ytd >= 0;
                const color = positive ? '#6ECF94' : '#EF6B6B';
                return (
                  <div
                    key={cat.key}
                    className="luxury-card p-4 hover:border-gold/30 transition-all"
                    data-testid={`category-tile-${cat.key}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-white text-sm font-medium">{cat.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{cat.source}</p>
                      </div>
                      <div className={`flex items-center gap-1 text-sm font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {positive ? '+' : ''}{cat.ytd}%
                      </div>
                    </div>
                    <Sparkline data={cat.spark} color={color} />
                  </div>
                );
              })}
              {(!charts.grid?.data) && (
                <>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-28 bg-white/5" />
                  ))}
                </>
              )}
            </div>
          </section>

          {/* Market Sentinel News Feed */}
          <section data-testid="news-feed">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-gold" />
                <h2 className="font-serif text-xl text-white">Market Sentinel</h2>
              </div>
              <button onClick={fetchNews} className="text-xs text-muted-foreground hover:text-gold flex items-center gap-1 transition-colors" data-testid="refresh-news-btn">
                <RefreshCw size={12} className={newsLoading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-6">AI-curated intelligence feed, refreshes every 3 days</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {newsLoading && newsArticles.length === 0 && (
                [0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-36 bg-white/5" />
                ))
              )}
              {!newsLoading && newsArticles.length === 0 && (
                <div className="col-span-full luxury-card p-8 text-center" data-testid="news-empty">
                  <Globe size={24} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">News feed unavailable. Try refreshing in a moment.</p>
                </div>
              )}
              {newsArticles.map((card, i) => (
                <div
                  key={i}
                  className="luxury-card p-5 block hover:border-gold/30 transition-all group"
                  data-testid={`news-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gold font-medium">{card.source}</span>
                    <span className="text-[10px] text-muted-foreground">{card.date}</span>
                  </div>
                  <h3 className="text-white text-sm font-medium mb-2 group-hover:text-gold transition-colors">
                    {card.headline}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                    {card.summary}
                  </p>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${
                    card.impact === 'high' 
                      ? 'text-emerald-400 bg-emerald-400/10' 
                      : 'text-gold bg-gold/10'
                  }`}>
                    {card.impact === 'high' ? 'High Impact' : 'Strategic'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}
